// تخصیص سرنخ (AP-ENG-02؛ US-ENG-004؛ BR-ENG-004): چرخشی بین مشاوران فروش و مرکز تماس، بر اساس دسته علاقه یا دستی؛ SLA نخستین تماس ۲ ساعت کاری
// (AC-ENG-004.1 و AC-ENG-004.2). نشان SLA در فهرست و جزئیات، تنظیم روش تخصیص و تخصیص دستی با اعلان به مسئول تازه.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type EngAssigneeOption,
  type EngLeadAssignmentMethod,
  type EngLeadAssignmentView,
  type EngLeadSummaryView,
  type EngLeadView,
  type EngOfferingOption,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormDialog, JalaliDate, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AssigneePicker, engKeys, OfferingPicker } from './common';

export const ASSIGNMENT_METHODS: readonly NonNullable<EngLeadAssignmentMethod>[] = [
  'RoundRobin',
  'ByCategory',
  'Manual',
];

type SlaSubject = Pick<EngLeadSummaryView, 'firstContactDueAt' | 'firstContactAt' | 'slaBreached'>;

/** نشان SLA نخستین تماس: «عقب از SLA» یا «موعد تماس» تا وقتی تماسی ثبت نشده. */
export function SlaChip({ lead, testId }: { lead: SlaSubject; testId?: string }) {
  const { t } = useTranslation('eng');
  if (lead.firstContactAt || !lead.firstContactDueAt) {
    return null;
  }
  if (lead.slaBreached) {
    return <Chip size="small" color="error" label={t('eng:sla.breached')} data-testid={testId} />;
  }
  return (
    <Tooltip title={t('eng:sla.due_hint')}>
      <Chip
        size="small"
        color="warning"
        variant="outlined"
        label={
          <>
            {t('eng:sla.due')} <JalaliDate value={lead.firstContactDueAt} withTime />
          </>
        }
        data-testid={testId}
      />
    </Tooltip>
  );
}

/** تخصیص دستی سرنخ (AC-ENG-004.1: اعلان به مسئول تازه). */
export function AssignLeadDialog({
  lead,
  onClose,
}: {
  lead: EngLeadView;
  onClose: (saved?: EngLeadView) => void;
}) {
  const { t } = useTranslation('eng');
  const { tenantApi } = useTenantSession();
  const [assignee, setAssignee] = useState<EngAssigneeOption | null>(
    lead.ownerMembershipId
      ? { membershipId: lead.ownerMembershipId, name: lead.ownerName ?? '', roles: [] }
      : null,
  );
  const [submitError, setSubmitError] = useState<unknown>();
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/engagement/leads/{id}:assign', {
          params: { path: { id: lead.id } },
          body: { ownerMembershipId: assignee?.membershipId ?? null },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: (saved) => onClose(saved),
    onError: (error) => setSubmitError(error),
  });
  return (
    <FormDialog
      open
      maxWidth="xs"
      screenId="SCR-STAFF-960-ASSIGN"
      titleKey="eng:assign_form.title"
      onSubmit={() => save.mutate()}
      onClose={() => onClose()}
      isDirty={assignee?.membershipId !== (lead.ownerMembershipId ?? undefined)}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="body2">{t('eng:assign_form.hint')}</Typography>
        <AssigneePicker
          value={assignee}
          onChange={setAssignee}
          labelKey="eng:assign_form.owner"
          testId="lead-assign-owner"
        />
      </Stack>
    </FormDialog>
  );
}

interface CategoryRuleRow {
  key: number;
  category: EngOfferingOption | null;
  member: EngAssigneeOption | null;
}

/** تنظیم روش تخصیص خودکار سرنخ‌های تازه (BR-ENG-004). */
export function LeadAssignmentSettingsDialog({
  onClose,
}: {
  onClose: (saved?: EngLeadAssignmentView) => void;
}) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const queryKey = [...engKeys.all(session?.tenantId), 'lead-assignment'];
  const settings = useQuery({
    queryKey,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/lead-assignment')),
  });
  const [method, setMethod] = useState<EngLeadAssignmentMethod | null>(null);
  const [rows, setRows] = useState<CategoryRuleRow[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const current = settings.data;
  const effectiveMethod = method ?? current?.method ?? 'RoundRobin';
  const effectiveRows: CategoryRuleRow[] =
    rows ??
    (current?.categoryRules ?? []).map((rule, index) => ({
      key: index,
      category: { id: rule.categoryId, name: rule.categoryName ?? '', kind: 'Category', price: null },
      member: { membershipId: rule.membershipId, name: rule.memberName ?? '', roles: [] },
    }));

  const update = (next: CategoryRuleRow[]) => {
    setRows(next);
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/engagement/lead-assignment', {
          body: {
            method: effectiveMethod,
            categoryRules: effectiveRows
              .filter((row) => row.category && row.member)
              .map((row) => ({ categoryId: row.category!.id, membershipId: row.member!.membershipId })),
            rowVersion: current?.rowVersion,
          },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      onClose(saved);
    },
    onError: (error) => setSubmitError(error),
  });

  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-960-ASSIGNMENT"
      titleKey="eng:assignment.title"
      onSubmit={() => save.mutate()}
      onClose={() => onClose()}
      isDirty={dirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      {settings.isPending && <LoadingState rows={3} />}
      {settings.error && <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />}
      {current && (
        <Stack spacing={2}>
          <RadioGroup
            value={effectiveMethod}
            onChange={(event) => {
              setMethod(event.target.value as EngLeadAssignmentMethod);
              setDirty(true);
            }}
            aria-label={t('eng:assignment.method')}
          >
            {ASSIGNMENT_METHODS.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio />}
                label={t(`eng:assignment_method.${value}`)}
                data-testid={`assignment-method-${value}`}
              />
            ))}
          </RadioGroup>
          <Typography variant="body2" color="text.secondary" data-testid="assignment-pool">
            {t('eng:assignment.pool', {
              roles: current.poolRoles
                .map((role) => t(`eng:assignment.role.${role}`, { defaultValue: role }))
                .join(t('common:list_separator')),
            })}
          </Typography>
          {effectiveMethod === 'ByCategory' && (
            <Stack spacing={1} data-testid="assignment-category-rules">
              <Typography variant="subtitle2">{t('eng:assignment.category_rules')}</Typography>
              {effectiveRows.map((row) => (
                <Stack
                  key={row.key}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <OfferingPicker
                    categories
                    value={row.category}
                    onChange={(category) =>
                      update(
                        effectiveRows.map((item) => (item.key === row.key ? { ...item, category } : item)),
                      )
                    }
                    labelKey="eng:assignment.category"
                    testId={`assignment-category-${row.key}`}
                  />
                  <AssigneePicker
                    value={row.member}
                    onChange={(member) =>
                      update(effectiveRows.map((item) => (item.key === row.key ? { ...item, member } : item)))
                    }
                    labelKey="eng:assignment.member"
                    testId={`assignment-member-${row.key}`}
                  />
                  <IconButton
                    aria-label={t('eng:assignment.remove')}
                    onClick={() => update(effectiveRows.filter((item) => item.key !== row.key))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() =>
                  update([
                    ...effectiveRows,
                    {
                      key: Math.max(-1, ...effectiveRows.map((row) => row.key)) + 1,
                      category: null,
                      member: null,
                    },
                  ])
                }
                data-testid="assignment-add-rule"
              >
                {t('eng:assignment.add_rule')}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {t('eng:assignment.category_fallback')}
              </Typography>
            </Stack>
          )}
          <Alert severity="info" data-testid="assignment-sla">
            {t('eng:assignment.sla', { hours: faDigits(Number(current.slaBusinessHours)) })}
          </Alert>
        </Stack>
      )}
    </FormDialog>
  );
}
